<script setup>
import { computed, ref, defineProps, onMounted, onUnmounted } from 'vue';

const searchQuery = ref('');
const props = defineProps({
    schema: {
        type: Object,
        required: true,
        default: () => ({ fields: [] })
    }
});

const itemsPerPageOptions = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 }
];

const tableRef = ref(null);
const currentPage = ref(1);
const itemsPerPage = ref(10);

const handleItemsPerPageChange = (value) => {
    itemsPerPage.value = Number(value);

    if (currentPage.value > totalPages.value) {
        currentPage.value = totalPages.value;
    }
};

const handleResize = () => {
};

onMounted(() => {  
    window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

const schemaFields = computed(() => props.schema?.fields || []);
const filteredSchemaFields = computed(() => {
    if (!searchQuery.value) return schemaFields.value;
    
    const query = searchQuery.value.toLowerCase();
    return schemaFields.value.filter(field => 
        field.name.toLowerCase().includes(query) ||
        field.type.toLowerCase().includes(query) ||
        (field.description && field.description.toLowerCase().includes(query))
    );
});
const totalItems = computed(() => filteredSchemaFields.value.length);
const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value));
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);
const endIndex = computed(() => startIndex.value + itemsPerPage.value);
const paginatedSchema = computed(() => {
    return filteredSchemaFields.value.slice(startIndex.value, endIndex.value);
});

const displayedPages = computed(() => {
    if (totalPages.value <= 7) return Array.from({length: totalPages.value}, (_, i) => i + 1);
    
    let pages = [];
    if (currentPage.value <= 3) {
        pages = [1, 2, 3, 4, '...', totalPages.value];
    } else if (currentPage.value >= totalPages.value - 2) {
        pages = [1, '...', totalPages.value - 3, totalPages.value - 2, totalPages.value - 1, totalPages.value];
    } else {
        pages = [1, '...', currentPage.value - 1, currentPage.value, currentPage.value + 1, '...', totalPages.value];
    }
    return pages;
});

const prevPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
};

const nextPage = () => {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
    }
};

const goToPage = (page) => {
    currentPage.value = page;
};
</script> 
<template>
    <div ref="tableRef" class="w-full h-full">
        <!-- Empty State -->
        <div v-if="!schemaFields.length" class="py-12 h-full flex flex-col items-center justify-center text-white/80">
            <i class="mdi mdi-table-off text-6xl"></i>
            <p class="text-lg">No schema available</p>
        </div>

        <!-- Desktop Table View -->
        <div v-else-if="schemaFields.length" class="block">
            <!-- Pagination section moved to top -->
            <div class="flex items-center px-4 py-2 bg-echoes-background-black border border-echoes-border-gray border-t-0 border-l-0 border-r-0">
                <div class="flex items-center justify-between space-x-4 w-full">
                    <div class="flex items-center space-x-4">
                        <div class="text-xs text-white/80"> 
                            Showing {{ startIndex + 1 }}-{{ Math.min(endIndex, totalItems) }} of {{ totalItems }} items
                        </div>
                        <div class="flex items-center space-x-2 border-l border-slate-600/50 pl-4">
                            <label for="itemsPerPage" class="text-xs text-white/80">Items per page:</label>
                            <select
                                id="itemsPerPage"
                                :value="itemsPerPage"
                                @change="handleItemsPerPageChange($event.target.value)"
                                class="bg-echoes-background-black border border-echoes-border-gray text-xs rounded-lg focus:ring-echoes-primary focus:border-echoes-primary p-1 dark:text-white appearance-none text-center"
                            >
                                <option
                                    v-for="option in itemsPerPageOptions"
                                    :key="option.value"
                                    :value="option.value"
                                >
                                    {{ option.label }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div class="flex items-center w-[250px]">
                        
                        <input
                            type="text"
                            id="schema-search"
                            v-model="searchQuery"
                            placeholder="Search by name, type or description..."
                            class="truncate w-full p-3 rounded-[8px] h-8 flex items-center text-xs
                                bg-echoes-background-black text-white/80
                                border border-echoes-border-gray
                                placeholder-white/40
                                transition-all duration-200 ease-in-out
                                focus:outline-none focus:ring-2 focus:ring-echoes-primary focus:border-echoes-primary
                                hover:bg-echoes-background-black/90 hover:border-echoes-border-gray"
                        />
                    </div>
                </div>
            </div>

            <div class="border border-t-0 border-l-0 border-r-0 border-echoes-border-gray overflow-x-auto">
                <table class="w-full border-collapse text-sm">

                    <thead class="bg-echoes-background-black">
                        <tr class="border-b border-echoes-border-gray">
                            <th class="px-4 py-3 2xl:py-5 text-left text-xs font-medium text-white/80 uppercase tracking-wider whitespace-nowrap">
                                Column name
                            </th>
                            <th class="px-4 py-3 2xl:py-5 text-left text-xs font-medium text-white/80 uppercase tracking-wider whitespace-nowrap">
                                Data type
                            </th>
                            <th class="px-4 py-3 2xl:py-5 text-left text-xs font-medium text-white/80 uppercase tracking-wider whitespace-nowrap">
                                Description
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-transparent divide-y divide-echoes-border-gray">
                        <tr v-for="(colName, index) in paginatedSchema" :key="index"
                            class="hover:bg-echoes-background-black/30 transition-colors duration-200">
                            <td class="px-4 py-6 text-sm font-normal text-white whitespace-nowrap max-w-[300px] lg:max-w-[200px]">
                                <span class="block truncate" :title="colName.name">{{ colName.name }}</span>
                            </td>
                            <td class="px-4 py-6 whitespace-nowrapw-full lg:max-w-[150px]">
                                <div class="flex">
                                    <span class="px-3 py-1 text-xs font-normal rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 whitespace-nowrap lg:truncate"
                                          :title="colName.type || 'No data type available'">
                                        {{ colName.type || 'No data type available' }}
                                    </span>
                                </div>
                            </td>
                            <td class="px-4 py-6 text-sm text-white/80 whitespace-nowrap lg:max-w-[200px] truncate">
                                <div class="whitespace-nowrap lg:truncate" :title="colName.description || 'Description not set'">
                                    {{ colName.description || 'Description not set' }}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination buttons section -->
            <div class="flex items-center px-4 py-2 2xl:py-4 bg-echoes-background-black border border-echoes-border-gray border-t-0 border-l-0 border-r-0">
                <div class="flex items-center justify-center w-full">
                    <div class="flex items-center space-x-2">
                        <button @click="prevPage" :disabled="currentPage === 1" 
                            class="text-sm rounded-[13px] transition-colors duration-200"
                            :class="[
                                currentPage === 1
                                    ? 'text-white/80 cursor-not-allowed'
                                    : 'text-white/80 hover:bg-echoes-background-black/30'
                            ]">
                            <i class="mdi mdi-chevron-left text-lg"></i>
                        </button>
                        
                        <template v-for="page in displayedPages" :key="page">
                            <span v-if="page === '...'" class="px-3 py-1 text-sm text-white/80">...</span>
                            <button v-else @click="goToPage(page)"
                                class="px-3 py-1 text-sm rounded-[7px] transition-colors duration-200"
                                :class="[
                                    currentPage === page
                                        ? 'bg-echoes-primary text-white hover:bg-echoes-primary/80'
                                        : 'text-white/80 hover:bg-echoes-background-black/30'
                                ]">
                                {{ page }}
                            </button>
                        </template>

                        <button @click="nextPage" :disabled="currentPage === totalPages"
                            class="text-sm rounded-[13px] transition-colors duration-200"
                            :class="[
                                currentPage === totalPages
                                    ? 'text-white/80 cursor-not-allowed'
                                    : 'text-white/80 hover:bg-echoes-background-black/30'
                            ]">
                            <i class="mdi mdi-chevron-right text-lg"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>